import { useEffect, useMemo, useRef, useState } from "react";
import { TASK_TYPE_OPTIONS } from "../../helpers/taskTypeStyles";
import { agendaTypeLegendStyle } from "../../helpers/calendar/personaAgendaColors";
import { client } from "../../hooks/Client";
import { isSuperAdminTenant } from "../../utils/superAdminTenant";
import {
  useFirestore,
  useFirestoreGeneral,
} from "../../firebase/useFirestore";
import useSettings from "../../hooks/Settings";
import roleColorByPersona, { FALLBACK_ROLE_COLOR } from "../../helpers/calendar/roleColorByPersona";
import { taskInLocation, sortLocations } from "../../helpers/locations/locationFilter";
import hasLocationFilter from "../../helpers/dashboardHelpers/hasLocationFilter";
import taskPersonaIds from "../../helpers/calendar/taskPersonaIds";
import { householdPlanningEnabled } from "../../helpers/board/resolveBoardView";
import {
  ownPersonaIds,
  isOnlyOwn,
  showMyAppointmentsToggle,
  myAppointmentsKey,
  readMyAppointments,
  saveMyAppointments,
} from "../../helpers/calendar/myAppointments";
import browserStorage from "../../utils/browserStorage";

/**
 * @typedef {import('../../hooks/firebase/TaskDto.jsx').Task} Task
 */

/* ---------------- helpers ---------------- */

const useOnClickOutside = (refs, handler, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const listener = (e) => {
      const clickedInside = refs.some(
        (r) => r?.current && r.current.contains(e.target)
      );
      if (clickedInside) return;
      handler(e);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [refs, handler, enabled]);
};

const getChipSummary = ({ ids, nameById, emptyLabel }) => {
  if (!ids || ids.length === 0) {
    return { chips: [], extra: 0, labelFallback: emptyLabel };
  }

  const labels = ids.map((id) => nameById.get(id) || id).filter(Boolean);

  const chips = labels.slice(0, 2);
  const extra = Math.max(0, labels.length - chips.length);

  return { chips, extra, labelFallback: `${labels.length} geselecteerd` };
};

/**
 * @param {Object} props
 * @param {Task[]} props.docs
 * @param {function} [props.onFiltersChange]
 * @param {function} props.children - Render prop with filtered docs, the type filter and `selectedPersonaIds` (het medewerkerfilter; leeg = iedereen)
 * @param {Array<object>} [props.locations] - De locaties van de organisatie; vanaf twee verschijnt het filter
 * @param {Record<string, string>} [props.locationByAdressId] - Huishouden-ID -> locatie, voor huisbezoeken
 * @param {string} [props.currentUserId] - `auth.ID` van de ingelogde gebruiker; zonder deze prop geen knop 'Mijn afspraken'
 */
const CalendarFilters = ({
  docs,
  onFiltersChange,
  children,
  initialSelectedTypes = [],
  locations,
  locationByAdressId,
  currentUserId,
}) => {
  // Hooks
  const settings = useSettings();
  const { impactFunctionality } = settings;

  // Locatie: alleen als de ouder er iets over weet. Zonder deze twee props
  // gedraagt dit component zich precies zoals hiervoor, zodat de slotpicker en
  // de agenda naast het huishoudenoverzicht niets merken.
  // AI-CONTEXT: See docs/features/huishouden-locaties.md
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const showLocations = hasLocationFilter(locations);

  // ✅ Hide these when impactFunctionality === false
  // Projectafspraken worden alleen in de beheeromgeving vastgelegd; elders zou
  // de chip altijd leeg blijven. De tenant staat vast bij het laden van de
  // pagina, dus hier is geen laadtoestand die de chip te vroeg kan tonen.
  // AI-CONTEXT: See docs/features/deccos-projectafspraken.md
  const hiddenTypes = useMemo(() => {
    const hidden = !impactFunctionality ? ["activityForm", "measureMoment"] : [];
    return isSuperAdminTenant(client) ? hidden : [...hidden, "projectFollowUp"];
  }, [impactFunctionality]);

  // ✅ Only visible types (drives UI + "all" logic)
  const VISIBLE_TYPES = useMemo(() => {
    return TASK_TYPE_OPTIONS.filter((t) => !hiddenTypes.includes(t.value));
  }, [hiddenTypes]);

  const allTypeValues = useMemo(
    () => VISIBLE_TYPES.map((t) => t.value),
    [VISIBLE_TYPES]
  );

  // -------------------------
  // Filter state
  // -------------------------
  const [selectedTypes, setSelectedTypes] = useState(initialSelectedTypes);

  // Keep selectedTypes valid when hiddenTypes changes
  useEffect(() => {
    setSelectedTypes((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      return prevArr.filter((t) => allTypeValues.includes(t));
    });
  }, [allTypeValues]);

  // [] means "all"
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  // UI state
  const [showFilters, setShowFilters] = useState(false);

  // Dropdown open state + search
  const [personaOpen, setPersonaOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [personaSearch, setPersonaSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");

  const personaWrapRef = useRef(null);
  const roleWrapRef = useRef(null);

  useOnClickOutside([personaWrapRef], () => setPersonaOpen(false), personaOpen);
  useOnClickOutside([roleWrapRef], () => setRoleOpen(false), roleOpen);

  // Firestore
  const personas = useFirestoreGeneral("Personas", "Status", "active");
  const userRoles = useFirestore("UserRoles");

  // 'Mijn afspraken' (verzoek Duurzame Vecht): het medewerkerfilter op jezelf.
  // AI-CONTEXT: See src/helpers/calendar/myAppointments.js
  const myIds = useMemo(
    () => ownPersonaIds(Array.isArray(personas) ? personas : [], currentUserId),
    [personas, currentUserId]
  );
  const myIdsKey = myIds.join("|");
  // Alleen waar de knop bestaat (FIXtool) wordt er iets onthouden of
  // teruggezet. Elders begint het medewerkerfilter zoals altijd op 'iedereen',
  // ook als iemand zichzelf aanvinkt. Leeg zolang de instellingen laden.
  const storageKey = currentUserId && householdPlanningEnabled(settings)
    ? myAppointmentsKey(client, currentUserId)
    : "";

  // De bewaarde keuze terugzetten zodra bekend is wie 'ik' ben. Eén keer per
  // gebruiker: daarna is het filter van de gebruiker zelf.
  const restoredForRef = useRef("");
  useEffect(() => {
    if (!storageKey || !myIdsKey || restoredForRef.current === storageKey) return;
    restoredForRef.current = storageKey;
    if (readMyAppointments(browserStorage(), storageKey)) {
      setSelectedPersonaIds(myIdsKey.split("|"));
    }
  }, [storageKey, myIdsKey]);

  // personaId -> { roleId, roleName, color }
  const roleByPersonaId = useMemo(
    () => roleColorByPersona(
      Array.isArray(personas) ? personas : [],
      Array.isArray(userRoles) ? userRoles : []
    ),
    [personas, userRoles]
  );

  // -------------------------
  // Lookup maps
  // -------------------------
  const personaNameById = useMemo(() => {
    const map = new Map();
    (personas || []).forEach((d) => {
      const id = d?.ID;
      const name = d?.Name;
      if (id && name) map.set(id, name);
    });
    return map;
  }, [personas]);

  // -------------------------
  // Build options from personas
  // -------------------------
  const personaOptions = useMemo(() => {
    const ids = (personas || []).map((p) => p?.ID).filter(Boolean);

    return ids.sort((a, b) => {
      const la = (personaNameById.get(a) || a).toLowerCase();
      const lb = (personaNameById.get(b) || b).toLowerCase();
      return la.localeCompare(lb);
    });
  }, [personas, personaNameById]);

  const filteredPersonaOptions = useMemo(() => {
    const q = personaSearch.trim().toLowerCase();
    if (!q) return personaOptions;
    return personaOptions.filter((id) =>
      (personaNameById.get(id) || id).toLowerCase().includes(q)
    );
  }, [personaOptions, personaSearch, personaNameById]);

  // -------------------------
  // Role lookup + options
  // -------------------------
  const roleNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(userRoles) ? userRoles : []).forEach((r) => {
      if (r?.ID) map.set(r.ID, r.Name || r.ID);
    });
    return map;
  }, [userRoles]);

  const roleColorById = useMemo(() => {
    const map = new Map();
    (Array.isArray(userRoles) ? userRoles : []).forEach((r) => {
      if (r?.ID) map.set(r.ID, r.Color || FALLBACK_ROLE_COLOR);
    });
    return map;
  }, [userRoles]);

  const roleOptions = useMemo(() => {
    const ids = (Array.isArray(userRoles) ? userRoles : [])
      .map((r) => r?.ID)
      .filter(Boolean);

    return ids.sort((a, b) => {
      const la = (roleNameById.get(a) || a).toLowerCase();
      const lb = (roleNameById.get(b) || b).toLowerCase();
      return la.localeCompare(lb);
    });
  }, [userRoles, roleNameById]);

  const filteredRoleOptions = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return roleOptions;
    return roleOptions.filter((id) =>
      (roleNameById.get(id) || id).toLowerCase().includes(q)
    );
  }, [roleOptions, roleSearch, roleNameById]);

  // -------------------------
  // Filtering docs
  // -------------------------
  const filteredDocs = useMemo(() => {
    const primary = (docs || []).filter((task) => {
      if (selectedTypes?.length && !selectedTypes.includes(task?.Type)) return false;

      // Iedereen die op de afspraak staat, niet alleen `PersonaID`. Dat veld
      // draagt bijna alleen roosterregels; een huisbezoek zet zijn mensen in
      // `TeamMembers`. Wie op zichzelf filterde zag daardoor zijn rooster en
      // geen enkel bezoek — bij Duurzame Vecht 0 van de 44, platformbreed 304
      // afspraken bij 12 organisaties. Verzoek Duurzame Vecht.
      const betrokkenen = taskPersonaIds(task);

      if (
        selectedPersonaIds.length > 0 &&
        !betrokkenen.some((id) => selectedPersonaIds.includes(id))
      )
        return false;

      if (selectedRoleIds.length > 0) {
        // Staat er meer dan één persoon op, dan telt de afspraak mee zodra
        // ÉÉN van hen de gekozen rol heeft — anders valt een bezoek weg zodra
        // er iemand van een andere rol bij komt.
        const rollen = betrokkenen
          .map((id) => roleByPersonaId[id]?.roleId)
          .filter(Boolean);
        if (!rollen.some((roleId) => selectedRoleIds.includes(roleId))) return false;
      }

      // Een roosterregel draagt zijn locatie zelf, een huisbezoek erft hem van
      // het huishouden. Eén van de twee is genoeg.
      if (!taskInLocation(task, selectedLocationId, locationByAdressId)) return false;

      return true;
    });

    // Re-include tasks that would render as overlay on a visible personaSchedule,
    // so planning a new task stays informed when the user has filtered the view
    // down to just personas.
    const personasInView = new Set(
      primary
        .filter((t) => t?.Type === 'personaSchedule' && t?.PersonaID)
        .map((t) => t.PersonaID)
    );

    if (personasInView.size === 0) return primary;

    const seen = new Set(primary.map((d) => d?.docid).filter(Boolean));
    const overlayExtras = (docs || []).filter((task) => {
      if (!task || task.Type === 'personaSchedule') return false;
      if (task.docid && seen.has(task.docid)) return false;
      const members = Array.isArray(task.TeamMembers) ? task.TeamMembers : [];
      if (members.length === 0) return false;
      return members.some((pid) => personasInView.has(pid));
    });

    return overlayExtras.length === 0 ? primary : [...primary, ...overlayExtras];
  }, [docs, selectedTypes, selectedPersonaIds, selectedRoleIds, roleByPersonaId, selectedLocationId, locationByAdressId]);

  useEffect(() => {
    if (!onFiltersChange) return;
    onFiltersChange({ selectedTypes, selectedPersonaIds, selectedRoleIds });
  }, [selectedTypes, selectedPersonaIds, selectedRoleIds, onFiltersChange]);

  // -------------------------
  // Type pills
  // -------------------------
  const toggleType = (typeValue) => {
    setSelectedTypes((prev) => {
      const prevIsAll = prev.length === 0 || prev.length === allTypeValues.length;
      if (prevIsAll) return [typeValue];

      const exists = prev.includes(typeValue);
      if (exists) {
        const next = prev.filter((v) => v !== typeValue);
        return next.length === 0 ? [] : next;
      }
      return [...prev, typeValue];
    });
  };

  // -------------------------
  // Dropdown checklist toggles
  // -------------------------
  // Elke wijziging van het medewerkerfilter werkt de bewaarde keuze bij: wie
  // zijn eigen chip wegklikt of een collega aanvinkt, heeft 'Mijn afspraken'
  // uitgezet en wil dat de volgende keer ook niet terugzien.
  /** @param {Array<string>} next - Het nieuwe medewerkerfilter; leeg is iedereen */
  const setPersonaSelection = (next) => {
    setSelectedPersonaIds(next);
    if (storageKey) saveMyAppointments(browserStorage(), storageKey, isOnlyOwn(next, myIds));
  };

  const togglePersona = (id) => {
    const prev = selectedPersonaIds;
    const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    setPersonaSelection(next);
  };

  const myAppointmentsOn = isOnlyOwn(selectedPersonaIds, myIds);
  const showMyAppointments = Boolean(currentUserId) && showMyAppointmentsToggle({
    moduleOn: householdPlanningEnabled(settings),
    ownIds: myIds,
    docs,
    selectedIds: selectedPersonaIds,
  });
  const toggleMyAppointments = () => setPersonaSelection(myAppointmentsOn ? [] : myIds);

  const toggleRole = (id) => {
    setSelectedRoleIds((prev) => {
      if (prev.length === 0) return [id];
      const exists = prev.includes(id);
      if (exists) {
        const next = prev.filter((x) => x !== id);
        return next.length === 0 ? [] : next;
      }
      return [...prev, id];
    });
  };

  const resetFilters = () => {
    setSelectedTypes([]);
    setPersonaSelection([]);
    setSelectedRoleIds([]);
    setPersonaSearch("");
    setRoleSearch("");
  };

  // -------------------------
  // Chips (top bar)
  // -------------------------
  const isAllTypesSelected =
    selectedTypes.length === 0 || selectedTypes.length === allTypeValues.length;

  const activeTypeLabels = useMemo(() => {
    const map = new Map(VISIBLE_TYPES.map((t) => [t.value, t.label]));
    return (selectedTypes || [])
      .filter((v) => map.has(v))
      .map((v) => ({
        value: v,
        label: map.get(v) || v,
      }));
  }, [selectedTypes, VISIBLE_TYPES]);

  const removeType = (typeValue) => {
    setSelectedTypes((prev) => {
      const next = prev.filter((v) => v !== typeValue);
      return next.length === 0 ? [] : next;
    });
  };

  const removePersonaId = (id) =>
    setPersonaSelection(selectedPersonaIds.filter((x) => x !== id));

  const removeRoleId = (id) =>
    setSelectedRoleIds((prev) => prev.filter((x) => x !== id));

  // -------------------------
  // Closed-button chip summary (inside dropdown button)
  // -------------------------
  const personaSummary = useMemo(() => {
    return getChipSummary({
      ids: selectedPersonaIds,
      nameById: personaNameById,
      emptyLabel: "Alle medewerkers",
    });
  }, [selectedPersonaIds, personaNameById]);

  const roleSummary = useMemo(() => {
    return getChipSummary({
      ids: selectedRoleIds,
      nameById: roleNameById,
      emptyLabel: "Alle rollen",
    });
  }, [selectedRoleIds, roleNameById]);

  const hasNonDefault =
    selectedPersonaIds.length > 0 ||
    selectedRoleIds.length > 0 ||
    selectedTypes.length > 0;

  return (
    <div className="agenda-layout">
      {/* Top bar */}
      <div className="agenda-topbar">
        <button
          type="button"
          className="agenda-topbar__toggle"
          onClick={() => setShowFilters((s) => !s)}
          aria-expanded={showFilters}
        >
          {showFilters ? "Verberg filters" : "Toon filters"}
        </button>

        {showMyAppointments && (
          <button
            type="button"
            className={`agenda-topbar__mine${myAppointmentsOn ? " agenda-topbar__mine--active" : ""}`}
            onClick={toggleMyAppointments}
            aria-pressed={myAppointmentsOn}
          >
            Mijn afspraken
          </button>
        )}

        <div className="agenda-topbar__active">
          {/* Type chips */}
          {!isAllTypesSelected &&
            activeTypeLabels.map((t) => {
              const style = agendaTypeLegendStyle(t.value);
              const chipVars = {
                "--task-chip-border": style.borderColor,
                "--task-chip-bg": style.backgroundColor,
                "--task-chip-text": style.textColor,
                "--task-chip-dot": style.color,
              };

              return (
                <button
                  key={`type-${t.value}`}
                  type="button"
                  className="task-chip"
                  style={chipVars}
                  onClick={() => removeType(t.value)}
                  title="Verwijder filter"
                >
                  <span className="task-chip__dot" />
                  <span className="task-chip__label">{t.label}</span>
                  <span className="task-chip__x" aria-hidden="true">
                    ×
                  </span>
                </button>
              );
            })}

          {/* Persona chips */}
          {selectedPersonaIds.length > 0 &&
            selectedPersonaIds.map((id) => (
              <button
                key={`persona-${id}`}
                type="button"
                className="task-chip task-chip--neutral"
                onClick={() => removePersonaId(id)}
                title="Verwijder medewerker filter"
              >
                <span className="task-chip__label">
                  Medewerker: {personaNameById.get(id) || id}
                </span>
                <span className="task-chip__x" aria-hidden="true">
                  ×
                </span>
              </button>
            ))}

          {/* Role chips */}
          {selectedRoleIds.length > 0 &&
            selectedRoleIds.map((id) => (
              <button
                key={`role-${id}`}
                type="button"
                className="task-chip task-chip--neutral"
                onClick={() => removeRoleId(id)}
                title="Verwijder rol filter"
              >
                <span
                  className="role-color-dot"
                  style={{ "--role-dot": roleColorById.get(id) || FALLBACK_ROLE_COLOR }}
                />
                <span className="task-chip__label">
                  Rol: {roleNameById.get(id) || id}
                </span>
                <span className="task-chip__x" aria-hidden="true">
                  ×
                </span>
              </button>
            ))}
        </div>

        <div className="agenda-topbar__actions">
          <button
            type="button"
            className="agenda-topbar__reset"
            onClick={resetFilters}
            disabled={!hasNonDefault}
            title="Reset filters"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="agenda-main">
        {showFilters && (
          <aside className="agenda-sidebar">
            {/* Locatie — alleen vanaf twee locaties; bij nul of één valt er
                niets te kiezen. Een weergavefilter: leeg is alles, en iedereen
                kan elke locatie kiezen. */}
            {showLocations && (
              <div className="agenda-sidebar__section">
                <div className="agenda-sidebar__label">Locatie</div>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  aria-label="Locatie"
                >
                  <option value="">Alle locaties</option>
                  {sortLocations(locations).map((loc) => (
                    <option key={loc.docid} value={loc.docid}>
                      {loc.Title || "Naamloze locatie"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Type */}
            <div className="agenda-sidebar__section">
              <div className="agenda-sidebar__label">Type</div>
              <div className="agenda-sidebar__types">
                {VISIBLE_TYPES.map((t) => {
                  const active = selectedTypes.includes(t.value);
                  const style = agendaTypeLegendStyle(t.value);

                  const typeVars = {
                    "--task-pill-border": style.borderColor,
                    "--task-pill-bg-active": style.backgroundColor,
                    "--task-pill-text-active": style.textColor,
                    "--task-pill-dot": style.color,
                  };

                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => toggleType(t.value)}
                      className={`task-pill ${active ? "task-pill--active" : ""}`}
                      style={typeVars}
                      title={t.label}
                    >
                      <span className="task-pill__dot" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Persona dropdown */}
            <div className="agenda-sidebar__section" ref={personaWrapRef}>
              <div className="agenda-sidebar__label">Medewerker</div>

              <button
                type="button"
                className="agenda-multiselect__button"
                onClick={() => setPersonaOpen((s) => !s)}
                aria-expanded={personaOpen}
              >
                <span className="agenda-multiselect__buttonInner">
                  {personaSummary.chips.length === 0 ? (
                    <span className="agenda-multiselect__placeholder">
                      {personaSummary.labelFallback}
                    </span>
                  ) : (
                    <span className="agenda-multiselect__chips">
                      {personaSummary.chips.map((label) => (
                        <span
                          key={label}
                          className="agenda-multiselect__miniChip"
                        >
                          {label}
                        </span>
                      ))}
                      {personaSummary.extra > 0 && (
                        <span className="agenda-multiselect__miniChip agenda-multiselect__miniChip--count">
                          +{personaSummary.extra}
                        </span>
                      )}
                    </span>
                  )}
                </span>

                <span className="agenda-multiselect__chev" aria-hidden="true">
                  ▾
                </span>
              </button>

              {personaOpen && (
                <div className="agenda-multiselect__menu">
                  <input
                    className="agenda-multiselect__search"
                    value={personaSearch}
                    onChange={(e) => setPersonaSearch(e.target.value)}
                    placeholder="Zoek medewerker…"
                    autoFocus
                  />

                  <div className="agenda-multiselect__list">
                    {filteredPersonaOptions.map((id) => {
                      const checked =
                        selectedPersonaIds.length > 0 &&
                        selectedPersonaIds.includes(id);

                      return (
                        <label key={id} className="agenda-multiselect__row">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePersona(id)}
                          />
                          <span>{personaNameById.get(id) || id}</span>
                        </label>
                      );
                    })}

                    {filteredPersonaOptions.length === 0 && (
                      <div className="agenda-multiselect__empty">
                        Geen resultaten
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Role dropdown */}
            <div className="agenda-sidebar__section" ref={roleWrapRef}>
              <div className="agenda-sidebar__label">Rol</div>

              <button
                type="button"
                className="agenda-multiselect__button"
                onClick={() => setRoleOpen((s) => !s)}
                aria-expanded={roleOpen}
              >
                <span className="agenda-multiselect__buttonInner">
                  {roleSummary.chips.length === 0 ? (
                    <span className="agenda-multiselect__placeholder">
                      {roleSummary.labelFallback}
                    </span>
                  ) : (
                    <span className="agenda-multiselect__chips">
                      {roleSummary.chips.map((label) => (
                        <span
                          key={label}
                          className="agenda-multiselect__miniChip"
                        >
                          {label}
                        </span>
                      ))}
                      {roleSummary.extra > 0 && (
                        <span className="agenda-multiselect__miniChip agenda-multiselect__miniChip--count">
                          +{roleSummary.extra}
                        </span>
                      )}
                    </span>
                  )}
                </span>

                <span className="agenda-multiselect__chev" aria-hidden="true">
                  ▾
                </span>
              </button>

              {roleOpen && (
                <div className="agenda-multiselect__menu">
                  <input
                    className="agenda-multiselect__search"
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    placeholder="Zoek rol…"
                    autoFocus
                  />

                  <div className="agenda-multiselect__list">
                    {filteredRoleOptions.map((id) => {
                      const checked =
                        selectedRoleIds.length > 0 &&
                        selectedRoleIds.includes(id);

                      return (
                        <label key={id} className="agenda-multiselect__row">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRole(id)}
                          />
                          <span
                            className="role-color-dot"
                            style={{ "--role-dot": roleColorById.get(id) || FALLBACK_ROLE_COLOR }}
                          />
                          <span>{roleNameById.get(id) || id}</span>
                        </label>
                      );
                    })}

                    {filteredRoleOptions.length === 0 && (
                      <div className="agenda-multiselect__empty">
                        Geen resultaten
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>
        )}

        <div className="agenda-content">{children({ filteredDocs, setSelectedTypes, selectedTypes, selectedPersonaIds })}</div>
      </div>
    </div>
  );
};

export default CalendarFilters;
